// src/recibosJspdf.ts
import jsPDF from 'jspdf';
import defaultLogo from '../../assets/images/logo_default.jpg';
import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

type Recibo = {
    code: number | string;
    value: string;
    name: string;
    paymentDate: string;
    corresponding: string;
    extensiveValue: string;
};

type JsPdfProviderProps = {
    receipts: any[];
    data: any;
    children: (args: {
        loading: boolean;
        error: Error | null;
        blob: Blob | null;
        url: string | null;
    }) => React.ReactNode;
};

const MARGIN_X = 15;
const PAGE_WIDTH = 210;
const HEADER_HEIGHT = 30;
const RECIBO_HEIGHT = 70;
const BLOCO_ALTURA = HEADER_HEIGHT + RECIBO_HEIGHT + 10; // header + recibo + espaço
const RECIBOS_POR_PAGINA = 2;

/**
 * Desenha o header (logo + dados da empresa)
 */
function desenharHeader(doc: jsPDF, data: any, offsetY: number) {
    const largura = PAGE_WIDTH - MARGIN_X * 2;

    // Caixa arredondada do header
    doc.setDrawColor(0);
    doc.roundedRect(MARGIN_X, offsetY, largura, HEADER_HEIGHT, 3, 3);

    // Logo
    const logoAlturaInPixels = 80;
    const logoLarguraInPixels = 150; // ajusta se quiser mais largo
    const logoAltura = logoAlturaInPixels * 0.264583;
    const logoLargura = logoLarguraInPixels * 0.264583; // ajusta se quiser mais largo
    const logoX = MARGIN_X + 6;
    const logoY = offsetY + (HEADER_HEIGHT - logoAltura) / 2;

    const logoSrc = data?.pathImage || defaultLogo;
    doc.addImage(logoSrc, 'JPEG', logoX, logoY, logoLargura, logoAltura);

    // Textos à direita
    const textXCenter = MARGIN_X + largura / 2 + 15; // empurra um pouco para a direita

    const configData = {
        phone: '047 98877-6655X',
        email: 'brudasubtil@gmail.com',
        address: 'Endereço: Rua Vitório Sens, 123 - Gruta - Ituporanga-SC',
        city: 'Ituporanga-SC',
    };

    if (data?.phone) {
        configData.phone = data.phone;
    }
    if (data?.email) {
        configData.email = data.email;
    }
    if (data?.address) {
        configData.address = data.address;
    }
    if (data?.city) {
        configData.city = data.city;
    }
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(configData.phone, textXCenter, offsetY + 10, { align: 'center' });

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(configData.email, textXCenter, offsetY + 16, {
        align: 'center',
    });

    const logoRightEdge = logoX + logoLargura;
    const padding = 1;
    const maxTextWidth = largura - logoRightEdge - padding; // deixa um espaço do lado do logo
    const addressLines = doc.splitTextToSize(configData.address, maxTextWidth);
    doc.text(addressLines, textXCenter, offsetY + 22, {
        align: 'center',
    });
}

/**logo
 * Desenha um recibo completo (header + caixa do recibo + linha pontilhada)
 */
function desenharRecibo(
    doc: jsPDF,
    recibo: Recibo,
    data: any,
    offsetY: number,
) {
    const largura = PAGE_WIDTH - MARGIN_X * 2;

    // Header da empresa
    desenharHeader(doc, data, offsetY);

    const yReciboTop = offsetY + HEADER_HEIGHT + 5;

    // Caixa principal do recibo
    doc.roundedRect(MARGIN_X, yReciboTop, largura, RECIBO_HEIGHT, 3, 3);

    // Topo com "RECIBO Nº" e "Valor"
    const boxAltura = 10;

    // Caixa do RECIBO Nº
    const reciboBoxLarg = 30;
    doc.setFillColor(230, 230, 230);
    recibo.value = formatValor(Number(recibo.value));
    const configLabelDataReceipt = {
        labelReceipt: 'RECIBO Nº:',
        numberReceipt: String(recibo.code),
        labelValue: 'Valor:',
        value: recibo.value,
    };
    const widthDataReceipt = {
        labelReceipt: doc.getTextWidth(configLabelDataReceipt.labelReceipt),
        numberReceipt: doc.getTextWidth(
            configLabelDataReceipt.numberReceipt.toString(),
        ),
        labelValue: doc.getTextWidth(configLabelDataReceipt.labelValue),
        value: doc.getTextWidth(configLabelDataReceipt.value.toString()),
    };
    const positionBoxNumberReceipt =
        MARGIN_X + widthDataReceipt.labelReceipt + 10;
    doc.roundedRect(
        positionBoxNumberReceipt,
        yReciboTop + 2,
        reciboBoxLarg,
        boxAltura,
        2,
        2,
        'F',
    );

    doc.setTextColor(0);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(configLabelDataReceipt.labelReceipt, MARGIN_X + 4, yReciboTop + 9);

    // Número dentro da caixa
    doc.setFont('helvetica', 'normal');
    doc.text(
        configLabelDataReceipt.numberReceipt,
        MARGIN_X + widthDataReceipt.labelReceipt + 10 + reciboBoxLarg / 2,
        yReciboTop + 9,
        {
            align: 'center',
        },
    );

    // Caixa do Valor
    const valorBoxLarg = widthDataReceipt.value + 8;
    doc.setFillColor(230, 230, 230);

    const resultRefPosition =
        widthDataReceipt.labelValue +
        6 +
        widthDataReceipt.value +
        2 +
        MARGIN_X +
        7;

    const positionLabel = (resultRefPosition - PAGE_WIDTH) * -1 - 1;
    doc.roundedRect(
        positionLabel + widthDataReceipt.labelValue + 4,
        yReciboTop + 2,
        valorBoxLarg,
        boxAltura,
        2,
        2,
        'F',
    );

    doc.setFont('helvetica', 'bold');
    doc.text('Valor:', positionLabel, yReciboTop + 9);

    doc.setFont('helvetica', 'normal');
    doc.text(
        recibo.value,
        positionLabel + widthDataReceipt.labelValue + 4 + valorBoxLarg / 2,
        yReciboTop + 9,
        {
            align: 'center',
        },
    );

    // Corpo do texto
    let cursorY = yReciboTop + boxAltura + 8;

    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');

    const texto = `No dia ${recibo?.paymentDate}, recebi(emos) de ${recibo.name}, a quantia de ${recibo.extensiveValue}. Correspondente a ${recibo.corresponding}, e para clareza firmo(amos) o presente.`;

    const maxWidth = largura - 8;
    const linhas = doc.splitTextToSize(texto, maxWidth);

    doc.text(linhas, MARGIN_X + 4, cursorY);

    cursorY += linhas.length * 5;

    const city = data?.city || 'Ituporanga-SC';
    const dataPorExtenso = format(new Date(), "dd 'de' MMMM 'de' yyyy", {
        locale: ptBR,
    });

    doc.text(`${city}, ${dataPorExtenso}`, MARGIN_X + 4, cursorY + 4);

    // Linha de assinatura
    const linhaAssinaturaY = yReciboTop + RECIBO_HEIGHT - 12;
    doc.line(
        MARGIN_X + 15,
        linhaAssinaturaY,
        MARGIN_X + largura - 15,
        linhaAssinaturaY,
    );

    doc.setFontSize(11);
    doc.text('Assinatura', PAGE_WIDTH / 2, linhaAssinaturaY + 6, {
        align: 'center',
    });

    // Linha pontilhada para recorte
    const dashedY = yReciboTop + RECIBO_HEIGHT + 2.5;
    doc.setLineDashPattern([2, 2], 0);
    doc.line(MARGIN_X, dashedY, MARGIN_X + largura, dashedY);
    doc.setLineDashPattern([], 0); // reseta
}

const formatValor = (v: number): string =>
    v.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
    });

/**
 * Gera o PDF e faz o download
 */
export function gerarRecibosPdf({
    dataset,
    data,
}: {
    dataset: Recibo[];
    data: any;
}) {
    const doc = new jsPDF('p', 'mm', 'a4');

    dataset.forEach((recibo, index) => {
        const indexNaPagina = index % RECIBOS_POR_PAGINA;

        if (index > 0 && indexNaPagina === 0) {
            doc.addPage();
        }

        const offsetY = 15 + indexNaPagina * BLOCO_ALTURA;
        desenharRecibo(doc, recibo, data, offsetY);
    });

    return doc;
}

function gerarRecibosInDoc(recibos: Recibo[], data: any) {
    const doc = new jsPDF('p', 'mm', 'a4');

    recibos.forEach((recibo, index) => {
        const indexNaPagina = index % RECIBOS_POR_PAGINA;

        if (index > 0 && indexNaPagina === 0) {
            doc.addPage();
        }

        const offsetY = 15 + indexNaPagina * BLOCO_ALTURA;
        desenharRecibo(doc, recibo, data, offsetY);
    });

    return doc;
}

export const JsPdfReciboProvider: React.FC<JsPdfProviderProps> = ({
    receipts,
    data,
    children,
}) => {
    const [blob, setBlob] = useState<Blob | null>(null);
    const [url, setUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        let cancelled = false;
        let oldUrl: string | null = null;

        const generate = async () => {
            setLoading(true);
            setError(null);

            try {
                const doc = gerarRecibosInDoc(receipts, data);
                const pdfBlob = doc.output('blob');

                if (cancelled) return;

                if (oldUrl) {
                    URL.revokeObjectURL(oldUrl);
                }

                const pdfUrl = URL.createObjectURL(pdfBlob);
                oldUrl = pdfUrl;

                setBlob(pdfBlob);
                setUrl(pdfUrl);
            } catch (e: any) {
                if (!cancelled) setError(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        generate();

        return () => {
            cancelled = true;
            if (oldUrl) {
                URL.revokeObjectURL(oldUrl);
            }
        };
    }, [receipts]);

    return <>{children({ loading, error, blob, url })}</>;
};
